import type {
  ArchiveContractRequest,
  ContractLineItem,
  ContractLineItemCreateRequest,
  ContractLineItemList,
  ContractLineItemPatchRequest,
  ContractRevenueSchedule,
  ContractRevenueScheduleCreateRequest,
  ContractRevenueScheduleList,
  ContractRevenueSchedulePatchRequest,
  ContractCreateRequest,
  ContractListQuery,
  ContractPatchRequest,
  RestoreContractRequest,
  TransitionContractRequest,
} from '@shared/types/contract';
import { api } from './client';

/// contract API クライアント = fetch のみ。Contract レスポンスは `unknown` を返す
/// (検証・branded 化は domain の `parseContract` / `parsePaged` 門番が行う / parse, don't validate / #471)。
/// line-item / revenue-schedule 系は別 endpoint の集計型なので従来どおり型付きで返す。

function buildQueryString(query: ContractListQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs.length === 0 ? '' : `?${qs}`;
}

export function listContracts(query: ContractListQuery = {}): Promise<unknown> {
  return api.get(`/api/contracts${buildQueryString(query)}`);
}

export function getContract(id: string): Promise<unknown> {
  return api.get(`/api/contracts/${encodeURIComponent(id)}`);
}

export function createContract(body: ContractCreateRequest): Promise<unknown> {
  return api.post('/api/contracts', body);
}

export function patchContract(id: string, body: ContractPatchRequest): Promise<unknown> {
  return api.patch(`/api/contracts/${encodeURIComponent(id)}`, body);
}

// 物理削除 (DELETE /api/contracts/{id}) はフロントから呼ばない。
// 誤削除防止のためアーカイブ方式へ移行済み (archiveContract / restoreContract)。
// バックエンドの DELETE は権限に関わらず 405 Method Not Allowed を返す (API から廃止)。

export function archiveContract(id: string, body: ArchiveContractRequest): Promise<unknown> {
  return api.post(`/api/contracts/${encodeURIComponent(id)}/archive`, body);
}

export function restoreContract(
  id: string,
  body: RestoreContractRequest = {},
): Promise<unknown> {
  return api.post(`/api/contracts/${encodeURIComponent(id)}/restore`, body);
}

export function transitionContract(
  id: string,
  body: TransitionContractRequest,
): Promise<unknown> {
  return api.post(`/api/contracts/${encodeURIComponent(id)}/transition`, body);
}

export async function listContractLineItems(
  contractId: string,
): Promise<ContractLineItemList> {
  return api.get<ContractLineItemList>(
    `/api/contracts/${encodeURIComponent(contractId)}/line-items`,
  );
}

export async function createContractLineItem(
  contractId: string,
  body: ContractLineItemCreateRequest,
): Promise<ContractLineItem> {
  return api.post<ContractLineItem>(
    `/api/contracts/${encodeURIComponent(contractId)}/line-items`,
    body,
  );
}

export async function patchContractLineItem(
  contractId: string,
  id: string,
  body: ContractLineItemPatchRequest,
): Promise<ContractLineItem> {
  return api.patch<ContractLineItem>(
    `/api/contracts/${encodeURIComponent(contractId)}/line-items/${encodeURIComponent(id)}`,
    body,
  );
}

export async function deleteContractLineItem(
  contractId: string,
  id: string,
): Promise<void> {
  return api.delete(
    `/api/contracts/${encodeURIComponent(contractId)}/line-items/${encodeURIComponent(id)}`,
  );
}

export async function listContractRevenueSchedules(
  contractId: string,
  lineItemId: string,
): Promise<ContractRevenueScheduleList> {
  return api.get<ContractRevenueScheduleList>(
    `/api/contracts/${encodeURIComponent(contractId)}/line-items/${encodeURIComponent(lineItemId)}/revenue-schedules`,
  );
}

export async function createContractRevenueSchedule(
  contractId: string,
  lineItemId: string,
  body: ContractRevenueScheduleCreateRequest,
): Promise<ContractRevenueSchedule> {
  return api.post<ContractRevenueSchedule>(
    `/api/contracts/${encodeURIComponent(contractId)}/line-items/${encodeURIComponent(lineItemId)}/revenue-schedules`,
    body,
  );
}

export async function patchContractRevenueSchedule(
  contractId: string,
  lineItemId: string,
  id: string,
  body: ContractRevenueSchedulePatchRequest,
): Promise<ContractRevenueSchedule> {
  return api.patch<ContractRevenueSchedule>(
    `/api/contracts/${encodeURIComponent(contractId)}/line-items/${encodeURIComponent(lineItemId)}/revenue-schedules/${encodeURIComponent(id)}`,
    body,
  );
}

export async function deleteContractRevenueSchedule(
  contractId: string,
  lineItemId: string,
  id: string,
): Promise<void> {
  return api.delete(
    `/api/contracts/${encodeURIComponent(contractId)}/line-items/${encodeURIComponent(lineItemId)}/revenue-schedules/${encodeURIComponent(id)}`,
  );
}
